import * as React from 'react';
import Slider from 'react-slick';

// Utils
import { useTranslation } from 'react-i18next';
import { isNilOrEmpty } from '@src/shared/src/util/general';
import useBreakpoints from '@toolkit/util/useBreakpoints';
// Constants
// Actions, Models & Interfaces
// Components
import Modal from '@toolkit/ui/Modal';
// Styles
import '../styles/HotelImageGallery.scss';

interface IProps {
  pictureURLs: string[];
  hotelName: string;
}

const HotelImageGallery: React.FC<IProps> = ({ pictureURLs, hotelName }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const sliderRef = React.useRef<Slider>(null);

  const { isMd } = useBreakpoints();

  if (isNilOrEmpty(pictureURLs)) {
    return null;
  }

  const displayImages = pictureURLs.slice(0, isMd ? 1 : 3);
  const totalPhotos = pictureURLs.length;

  const handleExpandClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentSlide(0);
  };

  React.useEffect(() => {
    if (isModalOpen && sliderRef.current) {
      // Reset slider to first slide when modal opens
      if (sliderRef.current) {
        (sliderRef.current as any).slickGoTo(0);
      }
    }
  }, [isModalOpen]);

  // Custom arrow components that filter out react-slick internal props
  const PrevArrow = ({ currentSlide, slideCount, ...props }: any) => (
    <button type="button" className="slick-prev" {...props}>
      <i className="material-symbols">arrow_back</i>
    </button>
  );

  const NextArrow = ({ currentSlide, slideCount, ...props }: any) => (
    <button type="button" className="slick-next" {...props}>
      <i className="material-symbols">arrow_forward</i>
    </button>
  );

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    centerPadding: '24px',
    centerMode: true,
    lazyLoad: 'ondemand',
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (index: number) => {
      setCurrentSlide(index);
    },
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  return (
    <div className="hotel-gallery">
      <div className="hotel-gallery-images" onClick={handleExpandClick}>
        {displayImages.map((url, index) => (
          <img src={url} alt={hotelName} key={`${index}-galery-image`} />
        ))}
        <button
          id="hotel-gallery-expand"
          className="hotel-gallery-expand"
          onClick={handleExpandClick}
          type="button">
          <i className="material-symbols">expand_content</i>
          {totalPhotos} {t('hotelImageGallery.label.photos')}
        </button>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        showCloseIcon={false}
        classNames={{
          overlay: 'hotel-gallery-modal-overlay',
          modal: 'hotel-gallery-modal',
        }}>
        <i className="material-symbols hotel-gallery-slider-close" onClick={handleCloseModal}>
          close
        </i>
        <div className="hotel-gallery-slider">
          <Slider ref={sliderRef} {...sliderSettings} className="hotel-gallery-slider-slick">
            {pictureURLs.map((url, index) => (
              <img src={url} alt={hotelName || 'Hotel image'} key={`${index}-slider-image`} />
            ))}
          </Slider>
          <div className="hotel-gallery-slider-counter">
            <span className="js-hotel-gallery-images-counter">{currentSlide + 1}</span>
            {' / '}
            {totalPhotos}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HotelImageGallery;
